import { withP5DrawingLibrary } from "../../mixins/p5DrawingLibrary";
import { pipeMixins } from "../../mixins/pipeMixins";
import { withScale } from "../../mixins/scale";
import { draw } from "./Visualisation/Site/draw";
import { setup } from "./Visualisation/Site/setup";
import { createPolygon } from "./createPolygon";
import { PolygonColours, PolygonTypes } from "./polygonTypes";

const withEditable = {
  editable() {
    return this._editable;
  },
  setEditable(editable) {
    this._editable = editable;
  },
};

const withLines = {
  lines() {
    return this._lines;
  },
  setLines(lines) {
    this._lines = lines;
  },
  selectedLine() {
    // loop through lines and return selected()
    for (let i = 0; i < this._lines.length; i++) {
      if (this._lines[i].selected()) {
        return this._lines[i];
      }
    }
    return null;
  },
  currentLine() {
    return this._currentLine;
  },
  setCurrentLine(currentLine) {
    this._currentLine = currentLine;
  },
};

const withPolygons = {
  polygons() {
    return this._polygons;
  },
  setPolygons(polygons) {
    this._polygons = polygons;
  },
  selectedPolygon() {
    // loop through polygons and return selected()
    for (let i = 0; i < this._polygons.length; i++) {
      if (this._polygons[i].selected()) {
        return this._polygons[i];
      }
    }
    return null;
  },
};

const withPlants = {
  selectPlant(plant) {
    this._selectedPlant = plant;
  },
  selectedPlant() {
    return this._selectedPlant;
  },
};

const withMode = {
  mode() {
    return this._mode;
  },
  setMode(mode) {
    this._mode = mode;
  },
  isCreateMode() {
    return this._mode === CanvasMode.CREATE;
  },
  isEditMode() {
    return this._mode === CanvasMode.EDIT;
  },
  isViewMode() {
    return this._mode === CanvasMode.VIEW;
  },
  setCreateMode() {
    this._mode = CanvasMode.CREATE;
  },
  setEditMode() {
    this._mode = CanvasMode.EDIT;
  },
  setViewMode() {
    this._mode = CanvasMode.VIEW;
  },
  setCreateLineMode() {
    this._mode = CanvasMode.CREATE_LINE;
  },
};

export const withCurrentPolygon = {
  currentPolygon() {
    return this._currentPolygon;
  },
  setCurrentPolygon(currentPolygon) {
    this._currentPolygon = currentPolygon;
  },
};

export const CanvasMode = {
  CREATE: "create",
  CREATE_LINE: "create_line",
  EDIT: "edit",
  VIEW: "view",
};

export const withDraggingVertex = {
  draggingVertex() {
    return this._draggingVertex || null;
  },
  setDraggingVertex(draggingVertex) {
    this._lastDraggedVertex = this._draggingVertex;
    this._draggingVertex = draggingVertex;
  },
  lastDraggedVertex() {
    return this._lastDraggedVertex || null;
  },
  setLastDraggedVertex(lastDraggedVertex) {
    this._lastDraggedVertex = lastDraggedVertex;
  },
};

const withPixelCoordinates = {
  pixelCenter() {
    return this._pixelCenter || { x: 0, y: 0 };
  },
  setPixelCenter(pixelCenter) {
    this._pixelCenter = pixelCenter;
  },
  xOffset() {
    return this._xOffset || 0;
  },
  setXOffset(xOffset) {
    this._xOffset = xOffset;
  },
  yOffset() {
    return this._yOffset || 0;
  },
  setYOffset(yOffset) {
    this._yOffset = yOffset;
  },
  setCenter({ x, y }) {
    this._xOffset = x;
    this._yOffset = y;
  },
  mouseX() {
    return this.drawingLibrary().mouseX - this.xOffset();
  },
  mouseY() {
    return this.drawingLibrary().mouseY - this.yOffset();
  },
};

const withZoom = {
  zoom() {
    return this._zoom || 1;
  },
  setZoom(zoom) {
    this._zoom = zoom;
  },
  zoomIn() {
    this._zoom = this._zoom * 1.1;
  },
  zoomOut() {
    this._zoom = this._zoom * 0.9;
  },
};

const withMPerPixel = {
  mPerPixel() {
    return this._mPerPixel;
  },
  setMPerPixel(mPerPixel) {
    this._mPerPixel = mPerPixel;
  },
};

export function createSite(
  containerElement,
  onPolygonsChange = () => {},
  onCurrentPolygonChange = () => {},
  plantList,
  options
) {
  const { framerate = 60, editable = true } = options;
  const visualisation = pipeMixins(
    withP5DrawingLibrary,
    withScale,
    withPolygons,
    withEditable,
    withMode,
    withCurrentPolygon,
    withLines,
    withDraggingVertex,
    withPlants,
    withPixelCoordinates,
    withZoom,
    withMPerPixel
  );
  visualisation.setPolygons([]);
  visualisation.setLines([]);
  visualisation.setCurrentPolygon([]);
  visualisation.setEditable(editable);
  visualisation.setMode(CanvasMode.EDIT);
  visualisation.setZoom(1);
  visualisation.setMPerPixel(0.1 * 0.26);
  // let polygons = []; // array to store polygons
  // indexs of locked polygons
  let lockedPolygons = []; //  these cant be affected until unlocked

  visualisation.destroy = () => {
    visualisation.destroyLibrary();
  };

  visualisation.onComplete = () => {
    // check if current polygon is complete
    if (visualisation.currentPolygon().length > 2) {
      // turn currentPolygon into polygon object
      let polygon = createPolygon(visualisation.currentPolygon());

      visualisation.polygons().push(polygon);
      onPolygonsChange(visualisation.polygons());
      visualisation.setCurrentPolygon([]);
      onCurrentPolygonChange(visualisation.currentPolygon());
      // set mode to edit
      visualisation.setEditMode();
    }
  };
  visualisation.onCancel = () => {
    // cancel current polygon
    visualisation.setCurrentPolygon([]);
    onCurrentPolygonChange(visualisation.currentPolygon());
    // set mode to edit
    visualisation.setEditMode();
  };

  visualisation.onDeleteVertex = () => {
    console.log("onDeleteVertex");
    // delete selected vertex
    if (visualisation.lastDraggedVertex() !== null) {
      if (visualisation.lastDraggedVertex().polygonIndex === -1) {
        // delete vertex from current polygon
        visualisation
          .currentPolygon()
          .splice(visualisation.lastDraggedVertex().vertexIndex, 1);
        onCurrentPolygonChange(visualisation.currentPolygon());
      } else {
        // delete vertex from polygon
        let polygon = visualisation
          .polygons()
          [visualisation.lastDraggedVertex().polygonIndex].verticies();
        polygon.splice(visualisation.lastDraggedVertex().vertexIndex, 1);
      }
      visualisation.setLastDraggedVertex(null);
      onPolygonsChange(visualisation.polygons());
    } else if (visualisation.currentPolygon() !== null) {
      console.log(visualisation.currentPolygon());
      // delete last vertex from current polygon
      visualisation.currentPolygon().pop();
      // if current polygon is empty then onCancel
      if (visualisation.currentPolygon().length === 0) {
        visualisation.onCancel();
      }
    }
  };

  visualisation.onDelete = () => {
    // delete selected polygon
    if (visualisation.selectedPolygon()) {
      // find index of polygon in polygons array
      let selectedPolygonIndex = visualisation

        .polygons()
        .findIndex((polygon) => polygon === visualisation.selectedPolygon());
      // remove polygon from polygons array
      visualisation.polygons().splice(selectedPolygonIndex, 1);
      selectedPolygonIndex = null;
    }
    // detele current polygon
    visualisation.setCurrentPolygon([]);
    onCurrentPolygonChange(visualisation.currentPolygon());
    onPolygonsChange(visualisation.polygons());
  };

  visualisation.setup = (drawingLibrary) => {
    setup(
      visualisation,
      drawingLibrary,
      containerElement,
      framerate,
      onPolygonsChange,
      onCurrentPolygonChange
    );
  };

  visualisation.draw = (drawingLibrary) => {
    draw(
      visualisation,
      drawingLibrary,
      visualisation.lastDraggedVertex(),
      visualisation.mPerPixel(),
      plantList
    );
  };

  visualisation.createLibrary(
    containerElement,
    visualisation.setup,
    visualisation.draw
  );

  return visualisation;
}
